(() => {
  const warrantySettings = window.bhiWarrantySettings || {};
  const warrantyVariantId = String(warrantySettings.variantId || '').trim();
  const warrantyAttributeName = warrantySettings.attributeName || 'Extended Warranty';
  const hasNumericWarrantyVariantId = /^\d+$/.test(warrantyVariantId);
  const sectionSelectors = {
    'cart-icon-bubble': '#cart-icon-bubble',
    'cart-notification-button': '#cart-notification-button',
  };

  let isReconciling = false;
  let queuedSync = false;

  function hasWarrantyConfig() {
    return hasNumericWarrantyVariantId;
  }

  function isWarrantyItem(item) {
    return String(item.variant_id || item.id || '') === warrantyVariantId;
  }

  function isWarrantySelected(cart) {
    return Boolean(String(cart.attributes?.[warrantyAttributeName] || '').trim());
  }

  function getCoveredSubtotalCents(cart) {
    return cart.items.reduce((sum, item) => {
      return isWarrantyItem(item) ? sum : sum + item.final_line_price;
    }, 0);
  }

  function getTargetWarrantyQuantity(cart) {
    const coveredSubtotalCents = getCoveredSubtotalCents(cart);
    if (coveredSubtotalCents <= 0) return 0;
    return Math.floor((coveredSubtotalCents + 5) / 10);
  }

  function getWarrantyLines(cart) {
    return cart.items
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => isWarrantyItem(item));
  }

  async function fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load cart (${response.status})`);
    }

    return response.json();
  }

  async function requestJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      const error = new Error(payload?.description || payload?.message || `Cart request failed (${response.status})`);
      error.status = response.status;
      error.payload = payload;
      error.url = url;
      throw error;
    }

    return payload;
  }

  async function updateWarrantyAttribute(value) {
    return requestJson(routes.cart_update_url, {
      attributes: {
        [warrantyAttributeName]: value,
      },
    });
  }

  async function resetWarrantySelection() {
    const warrantyToggle = document.querySelector(
      `[data-cart-attribute][data-attribute-name="${warrantyAttributeName}"]`
    );

    if (warrantyToggle) {
      warrantyToggle.checked = false;
    }

    try {
      await updateWarrantyAttribute('');
    } catch (attributeError) {
      console.error(attributeError);
    }
  }

  async function refreshFromCurrentCart() {
    try {
      const cart = await fetchCart();
      await refreshUi(cart);
    } catch (refreshError) {
      console.error(refreshError);
    }
  }

  async function addWarrantyLine(quantity) {
    return requestJson(routes.cart_add_url, {
      items: [
        {
          id: Number(warrantyVariantId),
          quantity,
        },
      ],
    });
  }

  async function changeLineQuantity(line, quantity) {
    return requestJson(routes.cart_change_url, {
      line,
      quantity,
    });
  }

  async function refreshSection(sectionId) {
    const target = document.querySelector(sectionSelectors[sectionId]);
    if (!target) return;

    const response = await fetch(`${routes.cart_url}?section_id=${sectionId}`);
    if (!response.ok) {
      throw new Error(`Failed to refresh section ${sectionId}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const source = doc.querySelector(sectionSelectors[sectionId]);
    if (source) {
      target.innerHTML = source.innerHTML;
    }
  }

  async function refreshUi(cartData) {
    await publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'warranty-sync',
      cartData,
      variantId: warrantyVariantId,
    });

    const refreshes = [refreshSection('cart-icon-bubble')];

    if (document.getElementById('cart-notification-button')) {
      refreshes.push(refreshSection('cart-notification-button'));
    }

    await Promise.allSettled(refreshes);
  }

  async function reconcile(options = {}) {
    if (!hasWarrantyConfig()) return false;

    if (isReconciling) {
      queuedSync = true;
      return false;
    }

    isReconciling = true;

    try {
      const cart = await fetchCart();
      const warrantySelected = isWarrantySelected(cart);
      const warrantyLines = getWarrantyLines(cart);
      const targetQuantity = getTargetWarrantyQuantity(cart);
      let mutated = false;

      if (!warrantySelected || targetQuantity === 0) {
        for (const { line } of [...warrantyLines].reverse()) {
          await changeLineQuantity(line, 0);
          mutated = true;
        }
      } else if (warrantyLines.length === 0) {
        await addWarrantyLine(targetQuantity);
        mutated = true;
      } else {
        const [primaryLine, ...extraLines] = warrantyLines;

        for (const { line } of [...extraLines].reverse()) {
          await changeLineQuantity(line, 0);
          mutated = true;
        }

        if (primaryLine.item.quantity !== targetQuantity) {
          await changeLineQuantity(primaryLine.line, targetQuantity);
          mutated = true;
        }
      }

      if (!mutated && !options.refreshUi) {
        return false;
      }

      const updatedCart = mutated ? await fetchCart() : cart;
      await refreshUi(updatedCart);
      return mutated;
    } catch (error) {
      console.error(error);

      if (error.status === 422) {
        await resetWarrantySelection();
        queuedSync = false;
        await refreshFromCurrentCart();
      }

      return false;
    } finally {
      isReconciling = false;

      if (queuedSync) {
        queuedSync = false;
        reconcile({ refreshUi: true });
      }
    }
  }

  window.BHIWarranty = {
    enabled: hasWarrantyConfig(),
    sync: reconcile,
  };

  if (warrantyVariantId !== '' && !hasWarrantyConfig()) {
    console.error('BHI warranty sync requires a numeric warranty variant ID.');
  }

  if (!hasWarrantyConfig()) return;

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
    if (event.source === 'warranty-sync') return;
    return reconcile();
  });

  document.addEventListener('DOMContentLoaded', () => {
    const warrantyToggle = document.querySelector(
      `[data-cart-attribute][data-attribute-name="${warrantyAttributeName}"]`
    );
    const hasWarrantyLine = document.querySelector('[data-warranty-line-item="true"]');

    if (warrantyToggle?.checked || hasWarrantyLine) {
      reconcile({ refreshUi: true });
    }
  });
})();
